import {useEffect, useState, ReactNode} from 'react';
import {getCognitoAuthUrl} from '../config/authConfig';
import {getIdToken, isTokenValid} from '../utils/authStorage';
import {DEV_AUTH_BYPASS} from '../lib/auth/devBypass';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({children}: ProtectedRouteProps) {
  const [isLoading, setIsLoading] = useState(!DEV_AUTH_BYPASS);
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_AUTH_BYPASS);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (DEV_AUTH_BYPASS) return;

    const checkAuth = async () => {
      try {
        const token = getIdToken();
        if (token && isTokenValid(token)) {
          setIsAuthenticated(true);
        } else {
          const authUrl = await getCognitoAuthUrl();
          window.location.href = authUrl;
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Authentication setup failed'
        );
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-background px-6">
        <div className="max-w-md text-center space-y-2">
          <h1 className="text-xl font-semibold">Authentication error</h1>
          <p className="text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground">
            Check your configuration and reload.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center space-y-2">
          <div className="w-10 h-10 mx-auto rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p className="text-sm text-muted-foreground">
            Redirecting to sign in…
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return <>{children}</>;
}
